/* Arte Europea — motion for the home and Story pages only.
   GSAP + ScrollTrigger + Lenis. Never runs in the theme editor or with reduced motion:
   the markup and CSS already show the final state. */
(() => {
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (!window.gsap || !window.ScrollTrigger) return;
  if ((window.Shopify && window.Shopify.designMode) || reduce || !document.documentElement.classList.contains('motion-ok')) return;

  const { gsap, ScrollTrigger } = window;
  gsap.registerPlugin(ScrollTrigger);
  gsap.defaults({ ease: 'power3.out', duration: 0.8 });

  /* ---------- Smooth scroll (one engine: Lenis) ---------- */
  if (window.Lenis && window.theme && window.theme.smoothScroll) {
    const lenis = new window.Lenis({ lerp: 0.14, smoothWheel: true, wheelMultiplier: 1, anchors: true });
    window.lenis = lenis;
    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add((time) => lenis.raf(time * 1000));
    gsap.ticker.lagSmoothing(0);
  }

  const contexts = new Map();

  /* ---------- Masked word reveal for large headings ---------- */
  function splitWords(el) {
    if (el.dataset.splitReady) return el.querySelectorAll('.mw');
    const text = el.textContent.trim();
    el.setAttribute('aria-label', text);
    el.innerHTML = text.split(/\s+/).map((w) => `<span class="mw-mask" aria-hidden="true"><span class="mw">${w.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]))}</span></span>`).join(' ');
    el.dataset.splitReady = 'true';
    return el.querySelectorAll('.mw');
  }

  /* ---------- Signature: close-up → painting → case silhouette → 3D case, one 360° turn ----------
     Key frames from the Claude Design handoff "Momento firma". The still layout (CSS) is frame 6; this
     reads it, starts on the close-up and plays towards it. The 3D case is optional and loaded on demand. */
  function supportsWebGL() {
    try {
      const c = document.createElement('canvas');
      return !!(window.WebGLRenderingContext && (c.getContext('webgl2') || c.getContext('webgl')));
    } catch (e) {
      return false;
    }
  }

  /* Smooth curve through key frames (cubic Hermite, zero speed at both ends) */
  function spline(times, values, t) {
    const n = times.length;
    if (t <= times[0]) return values[0];
    if (t >= times[n - 1]) return values[n - 1];
    let i = 0;
    while (t > times[i + 1]) i += 1;
    const slope = (k) => (values[k + 1] - values[k]) / (times[k + 1] - times[k]);
    const tangent = (k) => (k === 0 || k === n - 1 ? 0 : (slope(k - 1) + slope(k)) / 2);
    const h = times[i + 1] - times[i];
    const u = (t - times[i]) / h;
    const u2 = u * u;
    const u3 = u2 * u;
    return (2 * u3 - 3 * u2 + 1) * values[i] + (u3 - 2 * u2 + u) * h * tangent(i)
      + (-2 * u3 + 3 * u2) * values[i + 1] + (u3 - u2) * h * tangent(i + 1);
  }

  function signature(section) {
    const stage = section.querySelector('.sig__stage');
    const paint = section.querySelector('[data-sig-paint]');
    const caseEl = section.querySelector('[data-sig-case]');
    const copy = section.querySelector('[data-sig-copy]');
    const canvas = section.querySelector('[data-sig-canvas]');
    const img = paint && paint.querySelector('img');
    if (!stage || !paint || !caseEl || !copy || !img) return;

    const epic = section.querySelector('[data-sig-epic]');
    const epicLines = epic ? [...epic.querySelectorAll('.sig__epic-line')].map((line) => {
      if (!line.firstElementChild) line.innerHTML = `<span>${line.innerHTML}</span>`;
      return line.firstElementChild;
    }) : [];

    const fx = Number(section.dataset.focusX || 48) / 100;
    const fy = Number(section.dataset.focusY || 46) / 100;
    const zoomSetting = Math.max(2, Number(section.dataset.zoom || 4.5));
    const lowEnd = (navigator.deviceMemory && navigator.deviceMemory < 4) || (navigator.hardwareConcurrency && navigator.hardwareConcurrency < 4);
    const want3D = !!(section.dataset.case3d && canvas && supportsWebGL() && !lowEnd);
    const desktop = matchMedia('(min-width: 990px)');

    let tl;
    let viewer = null;
    let dragDeg = 0;
    let keys = null;
    const spin = { t: 0 };

    const applyPose = () => {
      if (!viewer || !keys) return;
      const t = spin.t;
      const at = (k) => spline(keys.times, keys[k], t);
      viewer.setPose({ ry: at('ry') + dragDeg, rx: at('rx'), rz: at('rz'), h: at('h'), cx: at('cx'), cy: at('cy') });
    };

    const build = () => {
      if (tl) { tl.scrollTrigger?.kill(); tl.kill(); }
      gsap.set([paint, caseEl, copy, canvas, epic].filter(Boolean), { clearProps: 'all' });

      const SW = stage.clientWidth;
      const SH = stage.clientHeight;
      const ratio = img.naturalWidth && img.naturalHeight ? img.naturalWidth / img.naturalHeight : 0.844;
      const wide = desktop.matches;

      // Frame 6 = the still layout
      const sr = stage.getBoundingClientRect();
      const cr = caseEl.getBoundingClientRect();
      const end = { w: cr.width, h: cr.height, x: cr.left - sr.left + cr.width / 2, y: cr.top - sr.top + cr.height / 2 };

      // Frame 3: the flat case, centred. Frame 2: the whole painting at the print's size (105 % of the case height)
      const h3 = (wide ? 0.7 : 0.474) * SH;
      const w3 = h3 * 0.496;
      const pH = 1.05 * h3;
      const pW = pH * ratio;
      paint.style.width = `${pW}px`;
      paint.style.height = `${pH}px`;
      const frame2 = { x: (SW - pW) / 2, y: (SH - pH) / 2, scale: 1 };
      const frame3 = { x: SW / 2 - w3 / 2 - 0.471 * w3, y: SH / 2 - h3 / 2 + 0.153 * h3 };
      // Frame 1: close-up on the focal point (eye and pearl), centred on the stage
      const zoom = Math.max(wide ? zoomSetting : zoomSetting * 0.7, Math.max(SW / pW, SH / pH) * 1.05);
      const frame1 = { x: SW / 2 - fx * pW * zoom, y: SH / 2 - fy * pH * zoom, scale: zoom };
      // The silhouette sits on its final spot in CSS; move it to the centre for frame 3
      const caseAt3 = { x: SW / 2 - end.x, y: SH / 2 - end.y, scale: h3 / end.h };

      keys = {
        times: [0, 0.28, 0.6, 1],
        ry: [0, 80, 200, 360],
        rx: [0, 12, 18, -3],
        rz: [0, -8, 8, 0],
        h: [h3 / SH, wide ? 0.66 : 0.46, wide ? 0.66 : 0.5, end.h / SH],
        cx: [0.5, wide ? 0.58 : 0.56, 0.46, end.x / SW],
        cy: [0.5, wide ? 0.45 : 0.42, wide ? 0.53 : 0.42, end.y / SH]
      };
      if (viewer) viewer.resize(SW, SH);

      tl = gsap.timeline({
        defaults: { ease: 'none' },
        scrollTrigger: {
          trigger: section, start: 'top top', end: 'bottom bottom', scrub: 0.35,
          onUpdate: (self) => { if (canvas) canvas.classList.toggle('is-interactive', !!viewer && self.progress > 0.97); }
        }
      });

      // 1 → 2: pull back from the eye to the whole painting
      tl.fromTo(paint, { x: frame1.x, y: frame1.y, scale: frame1.scale, autoAlpha: 1, force3D: true }, { x: frame2.x, y: frame2.y, scale: 1, duration: 0.2, ease: 'power2.inOut' }, 0)
        // 2 → 3: the painting slides onto the print framing; the case silhouette takes it, the rest dims and goes
        .to(paint, { x: frame3.x, y: frame3.y, duration: 0.13, ease: 'power2.inOut' }, 0.2)
        .fromTo(caseEl, { x: caseAt3.x, y: caseAt3.y, scale: caseAt3.scale, autoAlpha: 0 }, { autoAlpha: 1, duration: 0.07, ease: 'power1.inOut' }, 0.27)
        .to(paint, { autoAlpha: 0.14, duration: 0.06 }, 0.27)
        .to(paint, { autoAlpha: 0, duration: 0.02 }, 0.33)
        .fromTo(copy, { autoAlpha: 0, y: 16 }, { autoAlpha: 1, y: 0, duration: 0.09, ease: 'power2.out' }, 0.88);

      if (viewer) {
        // 3 → 6: hand-over to the 3D case at the same spot, then one turn on a leaning axis along an arc
        spin.t = 0;
        applyPose();
        tl.fromTo(canvas, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.012 }, 0.35)
          .to(caseEl, { autoAlpha: 0, duration: 0.001 }, 0.362)
          .fromTo(spin, { t: 0 }, { t: 1, duration: 0.5, onUpdate: applyPose }, 0.37);
      } else {
        // No 3D: the silhouette glides to its final place
        tl.to(caseEl, { x: 0, y: 0, scale: 1, duration: 0.45, ease: 'power2.inOut' }, 0.4);
      }

      // Statement: rises line by line behind the case, holds, then clears the way for the price
      if (epic) {
        tl.fromTo(epic, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.001 }, 0.4)
          .fromTo(epicLines, { yPercent: 105, rotate: 3 }, { yPercent: 0, rotate: 0, duration: 0.1, stagger: 0.035, ease: 'power3.out' }, 0.4)
          .fromTo(epic, { scale: 1.06 }, { scale: 0.97, duration: 0.42, ease: 'none' }, 0.4)
          .to(epicLines, { yPercent: -105, duration: 0.07, stagger: 0.02, ease: 'power2.in' }, 0.75)
          .to(epic, { autoAlpha: 0, duration: 0.001 }, 0.84);
      }
    };

    /* Drag to turn, once the scroll animation has finished */
    if (canvas) {
      let startX = 0;
      let startDeg = 0;
      let dragging = false;
      canvas.addEventListener('pointerdown', (e) => {
        if (!canvas.classList.contains('is-interactive')) return;
        dragging = true; startX = e.clientX; startDeg = dragDeg;
        canvas.setPointerCapture(e.pointerId);
        canvas.classList.add('is-dragging');
      });
      canvas.addEventListener('pointermove', (e) => {
        if (!dragging) return;
        dragDeg = startDeg + (e.clientX - startX) * 0.7;
        applyPose();
      });
      const release = () => {
        if (!dragging) return;
        dragging = false;
        canvas.classList.remove('is-dragging');
        // Ease back to the nearest front-facing turn
        const target = Math.round(dragDeg / 360) * 360;
        gsap.to({ v: dragDeg }, { v: target, duration: 0.9, ease: 'power3.out', onUpdate() { dragDeg = this.targets()[0].v; applyPose(); } });
      };
      canvas.addEventListener('pointerup', release);
      canvas.addEventListener('pointercancel', release);
      canvas.addEventListener('case3d:lost', () => { viewer = null; section.classList.remove('is-3d'); build(); ScrollTrigger.refresh(); });
    }

    section.classList.add('is-scrubbed');
    const start = () => { build(); ScrollTrigger.refresh(); };
    if (img.complete && img.naturalWidth) start();
    else img.addEventListener('load', start, { once: true });
    // Frames are measured in pixels: rebuild when the stage changes size (width only, so mobile URL bars don't trigger it)
    let lastW = stage.clientWidth;
    let resizeTimer;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => { if (stage.clientWidth !== lastW) { lastW = stage.clientWidth; start(); } }, 200);
    };
    window.addEventListener('resize', onResize);

    /* Load the 3D case only when the section is about to be seen */
    let io;
    if (want3D) {
      io = new IntersectionObserver(async (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        io.disconnect();
        try {
          const [mod, texture] = await Promise.all([
            import(section.dataset.case3d),
            new Promise((resolve, reject) => {
              const t = new Image();
              t.crossOrigin = 'anonymous';
              t.onload = () => resolve(t);
              t.onerror = reject;
              t.src = section.dataset.texture;
            })
          ]);
          await (document.fonts?.load?.('20px "Times New Roman"') || Promise.resolve());
          viewer = mod.createCase(canvas, { image: texture, brand: section.dataset.brand });
          if (!section.style.getPropertyValue('--print-bg')) section.style.setProperty('--print-bg', viewer.background);
          section.__case3d = viewer;
          section.classList.add('is-3d');
          start();
        } catch (err) {
          viewer = null; // keep the flat version
        }
      }, { rootMargin: '150% 0px' });
      io.observe(section);
    }

    return () => {
      io?.disconnect();
      window.removeEventListener('resize', onResize);
      tl?.scrollTrigger?.kill(); tl?.kill();
      viewer?.dispose();
      section.classList.remove('is-scrubbed', 'is-3d');
      gsap.set([paint, caseEl, copy, canvas, epic].filter(Boolean), { clearProps: 'all' });
    };
  }

  function initSection(root) {
    const ctx = gsap.context(() => {
      // Heading word reveals
      root.querySelectorAll('[data-split]').forEach((el) => {
        const words = splitWords(el);
        gsap.from(words, {
          yPercent: 110,
          duration: 0.9,
          stagger: 0.06,
          scrollTrigger: { trigger: el, start: 'top 82%', once: true }
        });
      });

      // Parallax on painting crops only (max 8%)
      root.querySelectorAll('.story [data-parallax] img, .hero [data-parallax]').forEach((el) => {
        const trigger = el.closest('section');
        gsap.fromTo(el, { yPercent: -4 }, {
          yPercent: 4,
          ease: 'none',
          scrollTrigger: { trigger, start: 'top bottom', end: 'bottom top', scrub: true }
        });
      });
    }, root);

    const cleanups = [() => ctx.revert()];
    root.querySelectorAll('[data-signature]').forEach((s) => {
      const c = signature(s);
      if (c) cleanups.push(c);
    });
    contexts.set(root, cleanups);
  }

  function destroySection(root) {
    (contexts.get(root) || []).forEach((fn) => fn());
    contexts.delete(root);
  }

  const boot = () => {
    document.querySelectorAll('#main > .shopify-section').forEach(initSection);
    window.addEventListener('load', () => ScrollTrigger.refresh());
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => ScrollTrigger.refresh());
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:section:load', (e) => { initSection(e.target); ScrollTrigger.refresh(); });
  document.addEventListener('shopify:section:unload', (e) => destroySection(e.target));
})();
